import { getBreadcrumbs, type Breadcrumb } from '../breadcrumbs/breadcrumbs';

type Severity = 'error' | 'warning' | 'info';

interface ReportInput {
  error: Error | { name: string; message: string; stack?: string };
  severity?: Severity;
  isHandled?: boolean;
  tags?: string[];
  sourceFile?: string;
  sourceLine?: number;
  sourceColumn?: number;
  lastApiCall?: { url: string; status?: number; responseExcerpt?: string };
}

interface OfficeContextInfo {
  host: string | null;
  platform: string | null;
  hostVersion: string | null;
  hostAppName: string | null;
  requirementSet: string | null;
  mailboxItemType: string | null;
}

interface AppStateSnapshot {
  selectionCount?: number;
  activeContainerId?: string | null;
  activeFolderId?: string | null;
  view?: string;
  modalOpen?: string | null;
}

interface ErrorPayload {
  occurredAt: string;
  sessionId: string;
  errorName: string;
  errorMessage: string;
  stackTrace: string | null;
  sourceFile: string | null;
  sourceLine: number | null;
  sourceColumn: number | null;
  host: string | null;
  platform: string | null;
  hostVersion: string | null;
  hostAppName: string | null;
  requirementSet: string | null;
  mailboxItemType: string | null;
  userAgent: string;
  addinVersion: string;
  breadcrumbs: Breadcrumb[];
  appStateSnapshot: AppStateSnapshot;
  lastApiCall: ReportInput['lastApiCall'] | null;
  networkOnline: boolean;
  severity: Severity;
  isHandled: boolean;
  tags: string[];
}

// Error reports go to the dedicated edge-functions host (same place every
// other endpoint lives), NOT the platform Supabase /functions/v1 gateway —
// our functions aren't deployed there and that path returned 500, so every
// report was silently dropped and the client_errors table stayed empty.
const REPORT_ENDPOINT = `${import.meta.env.VITE_OUTLOOK_FUNCTIONS_URL}/outlook-report-error`;
const PENDING_KEY = 'prism-pending-errors';
const THROTTLE_WINDOW_MS = 2000;

const sessionId = crypto.randomUUID();
const recentSignatures = new Map<string, number>();

let appStateProvider: () => AppStateSnapshot = () => ({});

export function registerAppStateProvider(fn: () => AppStateSnapshot): void {
  appStateProvider = fn;
}

function getOfficeContextInfo(): OfficeContextInfo {
  const office = (globalThis as { Office?: typeof Office }).Office;
  if (!office?.context) {
    return {
      host: null,
      platform: null,
      hostVersion: null,
      hostAppName: null,
      requirementSet: null,
      mailboxItemType: null,
    };
  }
  const ctx = office.context;
  const requirementSet = ctx.requirements?.isSetSupported?.('Mailbox', '1.15') ? '1.15+' : null;
  return {
    host: ctx.host ? String(ctx.host) : null,
    platform: ctx.platform ? String(ctx.platform) : null,
    hostVersion: ctx.diagnostics?.version ?? null,
    hostAppName: ctx.diagnostics?.host ? String(ctx.diagnostics.host) : null,
    requirementSet,
    mailboxItemType: ctx.mailbox?.item?.itemType ? String(ctx.mailbox.item.itemType) : null,
  };
}

function buildPayload(input: ReportInput): ErrorPayload {
  const officeInfo = getOfficeContextInfo();
  return {
    occurredAt: new Date().toISOString(),
    sessionId,
    errorName: input.error.name,
    errorMessage: input.error.message,
    stackTrace: input.error.stack ?? null,
    sourceFile: input.sourceFile ?? null,
    sourceLine: input.sourceLine ?? null,
    sourceColumn: input.sourceColumn ?? null,
    ...officeInfo,
    userAgent: navigator.userAgent,
    addinVersion: import.meta.env.VITE_APP_VERSION || '0.0.0',
    breadcrumbs: getBreadcrumbs(),
    appStateSnapshot: appStateProvider(),
    lastApiCall: input.lastApiCall ?? null,
    networkOnline: navigator.onLine,
    severity: input.severity ?? 'error',
    isHandled: input.isHandled ?? false,
    tags: input.tags ?? [],
  };
}

function signature(payload: ErrorPayload): string {
  return [payload.errorName, payload.sourceFile, payload.sourceLine].filter(Boolean).join('|');
}

function shouldThrottle(sig: string): boolean {
  const now = Date.now();
  const last = recentSignatures.get(sig);
  if (last && now - last < THROTTLE_WINDOW_MS) return true;
  recentSignatures.set(sig, now);
  for (const [key, ts] of recentSignatures) {
    if (now - ts > THROTTLE_WINDOW_MS * 10) recentSignatures.delete(key);
  }
  return false;
}

async function postPayload(payload: ErrorPayload): Promise<boolean> {
  if (!navigator.onLine) return false;
  try {
    const res = await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function enqueuePending(payload: ErrorPayload): void {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const queue: ErrorPayload[] = raw ? JSON.parse(raw) : [];
    queue.push(payload);
    localStorage.setItem(PENDING_KEY, JSON.stringify(queue.slice(-50)));
  } catch {
    // localStorage quota or disabled — give up silently
  }
}

export async function flushPending(): Promise<void> {
  const raw = localStorage.getItem(PENDING_KEY);
  if (!raw) return;
  let queue: ErrorPayload[];
  try {
    queue = JSON.parse(raw);
  } catch {
    localStorage.removeItem(PENDING_KEY);
    return;
  }
  const remaining: ErrorPayload[] = [];
  for (const payload of queue) {
    const ok = await postPayload(payload);
    if (!ok) remaining.push(payload);
  }
  if (remaining.length === 0) localStorage.removeItem(PENDING_KEY);
  else localStorage.setItem(PENDING_KEY, JSON.stringify(remaining));
}

export function reportError(input: ReportInput): void {
  const payload = buildPayload(input);
  if (shouldThrottle(signature(payload))) return;
  void postPayload(payload).then((ok) => {
    if (!ok) enqueuePending(payload);
  });
}

export function installGlobalHandlers(): void {
  window.addEventListener('error', (e) => {
    reportError({
      error: e.error ?? { name: 'WindowError', message: e.message },
      sourceFile: e.filename,
      sourceLine: e.lineno,
      sourceColumn: e.colno,
      isHandled: false,
      tags: ['window.onerror'],
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const err =
      reason instanceof Error ? reason : { name: 'UnhandledRejection', message: String(reason) };
    reportError({ error: err, isHandled: false, tags: ['unhandledrejection'] });
  });
  window.addEventListener('online', () => {
    void flushPending();
  });
}
